// Transaction utility functions for admin
import { IAdminTransaction } from '@/types/admin/transactions';

/**
 * Theme-aware palette consumed by status/amount color helpers below. Callers
 * derive this from `useThemeColors().semantic.status` + `useTransactionPalette()`
 * (or pass equivalents) so dark/light toggle re-tints transaction tables.
 */
export interface TransactionStatusPalette {
  success: string;
  error: string;
  pending: string;
  voided: string;
  fallback: string;
}

export interface TransactionAmountPalette {
  payment: string;
  charge: string;
}

/**
 * Get color for payment settlement status given a theme-aware palette.
 */
export const getPaymentStatusColor = (
  status: string,
  palette: TransactionStatusPalette,
): string => {
  const statusColors: { [key: string]: string } = {
    // Success states
    ACCEPTED: palette.success,

    // Pending states
    QUEUED_FOR_CAPTURE: palette.pending,

    // Failure states
    DECLINED: palette.error,
    REJECTED: palette.error,
    NOT_SUPPORTED: palette.error,

    // Requires attention
    INVALID_USER_DATA: palette.pending,

    // Cancelled/Inactive states
    VOIDED: palette.voided,
    LINE_DOWN: palette.voided,
  };

  return statusColors[status] || palette.fallback;
};

/**
 * Check if transaction is a payment
 */
export const isPayment = (transaction: IAdminTransaction): boolean => {
  return transaction.type === 'payment';
};

/**
 * Check if transaction is a charge
 */
export const isCharge = (transaction: IAdminTransaction): boolean => {
  return transaction.type === 'charge';
};

/**
 * Get color for transaction amount based on type, given a theme-aware palette.
 */
export const getTransactionAmountColor = (
  transaction: IAdminTransaction,
  palette: TransactionAmountPalette,
): string => {
  return isPayment(transaction) ? palette.payment : palette.charge;
};

/**
 * Get transaction type label
 */
export const getTransactionTypeLabel = (transaction: IAdminTransaction): string => {
  return isPayment(transaction) ? 'Payment' : 'Charge';
};

/**
 * Check if transaction has expandable details
 */
export const hasTransactionDetails = (transaction: IAdminTransaction): boolean => {
  return Boolean(
    (transaction.paidByFirstName && transaction.paidByLastName) ||
    transaction.paymentSettlementStatus ||
    (transaction.paymentConvenienceFee && transaction.paymentConvenienceFee > 0) ||
    transaction.postMonth ||
    transaction.arCode
  );
};

/**
 * Format payer name from transaction
 */
export const getPayerName = (transaction: IAdminTransaction): string => {
  if (transaction.paidByFirstName && transaction.paidByLastName) {
    return `${transaction.paidByFirstName} ${transaction.paidByLastName}`;
  }
  return '-';
};
